import React, { useEffect, useState } from 'react';
import { navigate } from 'gatsby';
import { useSelector, useDispatch } from 'react-redux';
import { addToCart } from '../../store/cart';

import LockIcon from '../../assets/icons/lock.svg';
import LockSubIcon from '../../assets/icons/sub-lock.svg';
import UnlockIcon from '../../assets/icons/open-lock.svg';
import UnlockSubIcon from '../../assets/icons/open-sub-lock.svg';
import NoDataImage from '../../assets/images/no-data.svg';
import YoutubeLogo from '../../assets/images/youtube_logo.png';
import { slugify } from '@src/utils/slugify';
import { DocumentLink } from '../DocumentLink';
import { BookmarkButton } from '../Bookmark';
import { trackDocumentClick } from '@src/utils/trackDocumentClick';

const PREDICTED_PAPERS_DOC_TYPE = 'Predicted Papers';
const PREDICTED_LIST_PAST_PAPER_TYPE = 'Predicted List';

const DocumentsList = ({ documents, showNoData, type, listColumns = '2' }) => {
  const miniCart = useSelector((state) => new Set(state.cart.miniCart));
  const ownedProducts = useSelector((state) => new Set(state.cart.ownedProducts));
  const user = useSelector((state) => state.user.data);
  const [price, setPrice] = useState('');

  const dispatch = useDispatch();

  const isSubscribed = user?.subscribed ?? false;

  useEffect(() => {
    if (type) {
      if (type.includes('Notes')) {
        setPrice(20);
      }
      if (type.includes('Worksheets')) {
        setPrice(25);
      }
      if (type.includes('Cheat Sheets')) {
        setPrice(15);
      }
    }
  }, [type]);
  const renderCoverImage = (module) => {
    let url = '';
    switch (module) {
      case 'a-level':
        url = '/a-level.png';
        break;
      case 'ks3-maths-years-7-8-and-9':
        url = '/years7-9.png';
        break;
      case '11-plus':
        url = '/11-plus.png';
        break;
      case 'ap-calculus':
        url = '/ap-calculus.png';
        break;
      case 'ap-statistics':
        url = '/ap-statistics.png';
        break;
      case 'gcse-igcse-o-level':
        url = '/gcse.png';
        break;
      case 'ib':
        url = '/ib.png';
        break;
      case 'university':
        url = '/university.png';
        break;
    }
    return <img src={url} />;
  };

  function mainButtonClick(id) {
    dispatch(addToCart(id, user));
  }

  const renderCoverPage = (document) => {
    let documentState = 'Buy';

    if (miniCart.has(document.id)) {
      documentState = 'in cart';
    }

    if (ownedProducts.has(document.id)) {
      documentState = 'Purchased';
    }

    if (document.isSubscription && isSubscribed && !ownedProducts.has(document.id))
      documentState = 'Open';

    const handleDocumentClick = () => {
      switch (documentState) {
        case 'Buy':
          trackDocumentClick(document, { source: 'legacy_list', outcome: 'cart', isSubscribed });
          mainButtonClick(document.id);
          break;
        case 'Purchased':
          trackDocumentClick(document, {
            source: 'legacy_list',
            outcome: 'profile',
            isSubscribed,
            destinationUrl: '/profile',
          });
          navigate('/profile');
          break;
        case 'Open':
          trackDocumentClick(document, {
            source: 'legacy_list',
            outcome: 'opened',
            isSubscribed,
            destinationUrl: `/embeded?id=${document.id}`,
          });
          navigate(`/embeded?id=${document.id}`);
          break;
        default:
          trackDocumentClick(document, {
            source: 'legacy_list',
            outcome: 'cart',
            isSubscribed,
            destinationUrl: '/cart',
          });
          navigate('/cart');
          break;
      }
    };

    const module = document.module?.slug;

    const goToDocument = () => {
      const destinationUrl =
        document.isSubscription && isSubscribed
          ? document?.videoGuide?.url
            ? `/embeded?id=${document.id}&videoGuide=${encodeURI(document?.videoGuide?.url)}`
            : `/embeded?id=${document.id}`
          : `/modules/${module}/${document.documentCategory
              .map((c) => c.slug)
              .join('/')}/${encodeURIComponent(slugify(document.name))}`;
      trackDocumentClick(document, {
        source: 'legacy_list',
        outcome: document.isSubscription && isSubscribed ? 'opened' : 'detail',
        isSubscribed,
        destinationUrl,
      });
      window.location.href = destinationUrl;
    };

    return (
      <div key={document.id} className="document-wrapper">
        <div className="document-image text-center">
          <button onClick={goToDocument}>
            {renderCoverImage(module)}
          </button>

          <div className="document-name category-option-link button-text">
            <button onClick={goToDocument}>
              {document.displayName || document.name}
            </button>
            {document.originalId && <BookmarkButton id={document.originalId} />}
          </div>
        </div>
        <span className="category-option-info srow y-centered action-bar x-space-between">
          {document.freeSample && (
            <>
              <span className="badge free-sample category-doc">FREE SAMPLE</span>
            </>
          )}

          {!document.freeSample && !document.freeDocument && document.hasPassword && (
            <>
              <>
                <span
                  className={`badge premium ${
                    document.isSubscription && isSubscribed ? 'strike-through' : ''
                  }`}
                >
                  £{document.price}
                </span>
                {documentState === 'Open' ? (
                  <img
                    src={UnlockSubIcon}
                    className="lock-icon"
                    alt="Unlock sub icon"
                    onClick={handleDocumentClick}
                  />
                ) : (
                  <>
                    <button
                      className={`button-b badge free-sample category-doc ${
                        document.isSubscription && isSubscribed ? 'green-button' : ''
                      }`}
                      onClick={handleDocumentClick}
                    >
                      {documentState}
                    </button>
                    {documentState === 'Purchased' ? (
                      <img
                        src={UnlockIcon}
                        className="lock-icon"
                        alt="Unlock sub icon"
                        onClick={handleDocumentClick}
                      />
                    ) : document?.isSubscription ? (
                      <img
                        src={LockSubIcon}
                        className="lock-icon"
                        alt="Unlock sub icon"
                        onClick={() => {
                          trackDocumentClick(document, {
                            source: 'legacy_list',
                            outcome: 'subscribe',
                            isSubscribed,
                            destinationUrl: '/subscriptions',
                          });
                          navigate('/subscriptions');
                        }}
                      />
                    ) : (
                      <img
                        src={LockIcon}
                        className="lock-icon"
                        alt="Unlock sub icon"
                        onClick={handleDocumentClick}
                      />
                    )}
                  </>
                )}
              </>
              <>
                {document.previewVideo && (
                  <div className="flex flex-center">
                    <img src={YoutubeLogo} style={{ width: '2rem', marginRight: '0.5rem' }} />
                    <a href={document.previewVideo} target="_blank" style={{ fontSize: '1rem' }}>
                      Preview Video
                    </a>
                  </div>
                )}
              </>
            </>
          )}
        </span>
      </div>
    );
  };

  const renderPredictedColumns = (columns) => (
    <div className="documents-predicted-columns">
      {Object.keys(columns).map((columnName) => (
        <div className="documents-predicted-column" key={columnName}>
          <span className="documents-predicted-column-header">{columnName}</span>
          <div className="documents-predicted-column-list">
            {columns[columnName].map(renderCoverPage)}
          </div>
        </div>
      ))}
    </div>
  );

  const renderNoData = () => (
    <div className="no-data">
      <img src={NoDataImage} className="no-data-image" alt="Category optiom" />
      <p className="no-data-text">
        {`No documents here yet but we're working really hard to provide them soon!

        You can sign up to our newsletter in the meantime to get informed about new releases.`}
      </p>
    </div>
  );

  const renderLink = (document) => (
    <DocumentLink
      key={document.id}
      document={document}
      isSubscribed={isSubscribed}
      fallbackPrice={price}
      action={<BookmarkButton id={document.originalId} />}
    />
  );

  const renderDoc = (document) => {
    const isFree =
      document.freeDocument ||
      document.freeSample ||
      (document.hasPassword && document.freeDocument);
    return isFree ? renderLink(document) : renderCoverPage(document);
  };

  const freeDocs = documents.filter((doc) => !doc.hasPassword || doc.freeDocument);
  const paidDocs = documents.filter((doc) => !doc.freeDocument && doc.hasPassword);

  // Predicted Papers are laid out in columns grouped by docPastPaperType. Any other
  // paid document (including predicted papers without a docPastPaperType) stays in the grid.
  const isPredictedColumnDoc = (doc) =>
    doc.docType?.name === PREDICTED_PAPERS_DOC_TYPE &&
    !!doc.docPastPaperType?.name &&
    doc.docPastPaperType.name !== PREDICTED_LIST_PAST_PAPER_TYPE;

  const predictedColumnDocs = paidDocs.filter(isPredictedColumnDoc);
  const ungroupedPaidDocs = paidDocs.filter((doc) => !isPredictedColumnDoc(doc));

  // Group by docPastPaperType.name. Iterating in source order means columns appear
  // left-to-right in first-appearance order (the grid's reading order).
  const predictedColumns = predictedColumnDocs.reduce((acc, doc) => {
    const key = doc.docPastPaperType.name;
    acc[key] = acc[key] || [];
    acc[key].push(doc);
    return acc;
  }, {});

  // Sort each column alphabetically by name (= the documents' grid order).
  Object.values(predictedColumns).forEach((docs) =>
    docs.sort((a, b) => a.name.localeCompare(b.name)),
  );

  console.log('docs: ', documents);

  return (
    <div>
      {showNoData && !documents.length && renderNoData()}
      {freeDocs && freeDocs.length > 0 && (
        <ul
          className={`category-options ${
            listColumns === '3' ? 'category-options-three-columns' : ''
          }`}
        >
          {freeDocs.map(renderDoc)}
        </ul>
      )}
      {predictedColumnDocs.length > 0 && renderPredictedColumns(predictedColumns)}
      {ungroupedPaidDocs.length > 0 && (
        <div
          className={`documents ${
            ungroupedPaidDocs.length === 3 ? 'documents-three-columns' : ''
          }`}
        >
          {ungroupedPaidDocs.map(renderDoc)}
        </div>
      )}
    </div>
  );
};

export default DocumentsList;
